import { Injectable } from "@angular/core";
import { DomSanitizer } from "@angular/platform-browser";
import { ContentType, ItemTagStatusType, SortDirection } from "../shared/contentTypes";
import { ArticleTagDataModel, ItemTagStorageViewModel, ItemTagViewModel } from "../shared/models";
import { SortOption } from "../shared/models/sort.model";
import { UserInfoData } from "../shared/models/userInfo.model";
import { PagingHelper } from "../shared/pagingHelper";
import { CircularInjectionHelper } from "./circularInjectionHelper";
import { IObjectObserver } from "./IObjectObserver";
import { ObjectObserverFactory } from "./objectObserverFactory";
import { UserHelperService } from "./userHelper.service";

@Injectable({
  providedIn: "root"
})
export class ItemTagHelperService {

  private tagSortOption: SortOption[];
  private pagingHelper: PagingHelper;

  private _itemTags: ItemTagStorageViewModel[];
  public get itemTags(): ItemTagStorageViewModel[] {
    return this._itemTags;
  }
  public set itemTags(v: ItemTagStorageViewModel[]) {
    this._itemTags = v;
  }


  private _articleTagObsServ: IObjectObserver<ArticleTagDataModel>;

  constructor(private _objObsInjectHelper: CircularInjectionHelper
    , private _domSanitizer: DomSanitizer
    , private _userHelperService: UserHelperService) {
    this._articleTagObsServ = ObjectObserverFactory.GetObjectObserverByInjectionHelper<ArticleTagDataModel>(ContentType.ProductTag, _objObsInjectHelper);

  }

  /**get the list of Itentags
 * if there is an error, just resolve null to load the page without tags
 */
  public GetProductTags(): Promise<ItemTagViewModel[]> {
    const me = this;
    return new Promise<ItemTagViewModel[]>((resolve, reject) => {
      me._articleTagObsServ.GetObjectsByFilter(tag => tag.active && !tag.deleted)
        .then(async (response) => {
          if (response) {
            const itemTagsViewModel = me.mapToItemTagViewModelList(response);
            me.tagSorting(itemTagsViewModel);
            await me.setItemTagsStatus(itemTagsViewModel);
            resolve(itemTagsViewModel);
          } else {
            resolve(null);
          }
        })
        .catch(e => {
          me._objObsInjectHelper.coreLogService.error("ItemTagHelperService.GetProductTags", "_articleTagObsServ.GetObjectsByFilter", e);
          resolve(null);
        })
    });
  }

  /**check if there is liked or unliked tag
  * used to sort products base on liked and disliked tags
  */
  public hasTagSorting(itemTags: ItemTagViewModel[]) {
    const me = this;
    return itemTags && itemTags.length > 0 && itemTags.some(t => t.itemTagStatus !== ItemTagStatusType.neutral);
  }

  /**check if there is disliked tag */
  public hasDislikeTag(itemTags: ItemTagViewModel[]) {
    const me = this;
    return itemTags && itemTags.length > 0 && itemTags.some(t => t.itemTagStatus === ItemTagStatusType.dislike);
  }

  /**update _itemtag by closing the tag Modal */
  public saveItemTagsOnStorage(itemTags: ItemTagViewModel[]): void {
    const me = this;
    const itemTagStorageViewModelList: ItemTagStorageViewModel[] = [];
    itemTags.forEach(itemTag => {
      if (itemTag.itemTagStatus !== ItemTagStatusType.neutral) {
        const itemTagStorageViewModel = new ItemTagStorageViewModel();
        itemTagStorageViewModel.id = itemTag.id;
        itemTagStorageViewModel.itemTagStatus = itemTag.itemTagStatus;
        itemTagStorageViewModelList.push(itemTagStorageViewModel);
      }
    });
    me._itemTags = itemTagStorageViewModelList;
  }

  public resetItemTagsOnStorage(): void {
    const me = this;
    me._itemTags = [];
  }

  public svgSanitizer(svg: string, color: string = "") {
    const me = this;
    if (color) {
      svg = svg.replace("<svg", `<svg style='fill: ${color};stroke: ${color};'`)
    }
    return me._domSanitizer.bypassSecurityTrustHtml(svg);
  }

  /**save tagId on user data */
  public saveUserTags(itemTags: ItemTagViewModel[]): Promise<void> {
    const me = this;
    return new Promise<void>(async (resolve, reject) => {
      try {
        const userinfo = await me._objObsInjectHelper.yourAppServer.User.GetUserInfo(false);
        const tagsStatus: { [id: number]: ItemTagStatusType } = {};
        // Start with previous userData, this will contain inactive and deleted tags
        userinfo.tag_disliked_ids.forEach(id => tagsStatus[id] = ItemTagStatusType.dislike);
        userinfo.tag_liked_ids.forEach(id => tagsStatus[id] = ItemTagStatusType.like);
        // Overwrite with new Info
        itemTags.forEach(tag => tagsStatus[tag.id] = tag.itemTagStatus);

        const likeIds: number[] = [];
        const dislikeIds: number[] = [];
        for (const [id, status] of Object.entries(tagsStatus)) {
          if (status === ItemTagStatusType.like) {
            likeIds.push(+id);
          } else if (status === ItemTagStatusType.dislike) {
            dislikeIds.push(+id);
          }
        }
        await me._userHelperService.SetCustomerArtikelTags(likeIds, dislikeIds);
        me.updateServiceByUserInfo(likeIds, dislikeIds);
        await me._objObsInjectHelper.yourAppServer.User.GetUserInfo(true);
        resolve();
      } catch (error) {
        this._objObsInjectHelper.coreLogService.error("BillingInfoService.saveCustomerMainAddressDataModel", "try catch", error);
        reject();
      }
    })
  }

  /**set tag status base on userinfo if user is logged in 
   * if user is not logged in set tag status by _itemTag from this service
   */
  private async setItemTagsStatus(itemTagsViewModel: ItemTagViewModel[]): Promise<void> {
    const me = this;
    try {
      if (me._userHelperService.IsRegistered) {
        const userInfo = await me._objObsInjectHelper.yourAppServer.User.GetUserInfo(true);
        if (userInfo) {
          me.setTagStatusByUserInfo(userInfo, itemTagsViewModel);
        }
      } else {
        if (me._itemTags && me._itemTags.length > 0) {
          me._itemTags.forEach(tagStorage => {
            itemTagsViewModel.find(i => i.id === tagStorage.id)!.itemTagStatus = tagStorage.itemTagStatus;
          });
        }
      }
    } catch (e) {
      me._objObsInjectHelper.coreLogService.error("ItemTagHelperService.setItemTagsStatus", "valueStorage.get", e);
    }
  }

  /**set status tag list by user info tag list */
  private setTagStatusByUserInfo(userInfoData: UserInfoData, itemTagsViewModel: ItemTagViewModel[]) {
    const me = this;
    itemTagsViewModel.forEach(tag => {
      if (userInfoData.tag_liked_ids.includes(tag.id)) {
        tag.itemTagStatus = ItemTagStatusType.like;
      } else if (userInfoData.tag_disliked_ids.includes(tag.id)) {
        tag.itemTagStatus = ItemTagStatusType.dislike;
      } else {
        tag.itemTagStatus = ItemTagStatusType.neutral;
      }
    });
    me.updateServiceByUserInfo(userInfoData.tag_liked_ids, userInfoData.tag_disliked_ids);
  }

  /**update _itemTag by the list of user like and dislike */
  private updateServiceByUserInfo(likedIds: number[], dislikedIds: number[]): void {
    const me = this;
    const itemTagStorageViewModelList: ItemTagStorageViewModel[] = [];
    if (likedIds && likedIds.length > 0) {
      likedIds.forEach(likeId => {
        const itemTagStorageViewModel = new ItemTagStorageViewModel();
        itemTagStorageViewModel.id = likeId;
        itemTagStorageViewModel.itemTagStatus = ItemTagStatusType.like;
        itemTagStorageViewModelList.push(itemTagStorageViewModel);
      });
    };
    if (dislikedIds && dislikedIds.length > 0) {
      dislikedIds.forEach(dislikeId => {
        const itemTagStorageViewModel = new ItemTagStorageViewModel();
        itemTagStorageViewModel.id = dislikeId;
        itemTagStorageViewModel.itemTagStatus = ItemTagStatusType.dislike;
        itemTagStorageViewModelList.push(itemTagStorageViewModel);
      });
    };
    me._itemTags = itemTagStorageViewModelList;
  }

  /**sorting the tags base on sortnumber */
  private tagSorting(itemTags: ItemTagViewModel[]): ItemTagViewModel[] {
    const me = this;
    me.pagingHelper = new PagingHelper();
    me.tagSortOption = [];
    me.tagSortOption.push(me.pagingHelper.createSortOption("sortNumber", SortDirection.ASC));
    itemTags.sort(me.pagingHelper.fieldSorter(me.tagSortOption));
    return itemTags;
  }

  private mapToItemTagViewModel(model: ArticleTagDataModel): ItemTagViewModel {
    const me = this;
    const viewModel = new ItemTagViewModel();
    viewModel.id = model.id;
    viewModel.label_de = model.name_de;
    viewModel.label_en = model.name_en;
    viewModel.svgIcon = model.svg;
    viewModel.sortNumber = model.sort_no;
    viewModel.color = model.color;
    viewModel.itemTagStatus = ItemTagStatusType.neutral;
    return viewModel;
  }

  public mapToItemTagViewModelList(modelList: ArticleTagDataModel[]): ItemTagViewModel[] {
    const me = this;
    if (modelList && modelList.length > 0) {
      const result: ItemTagViewModel[] = [];
      modelList.forEach(model => {
        result.push(me.mapToItemTagViewModel(model));
      });
      return result;
    } else {
      return null;
    }
  }
}